import React, { useState, useCallback, useRef } from "react";
import { useStoreSelector } from "~/lib/store/hooks";
import { tenantOnboardingStore } from "../../store/tenantOnboardingStore";
import { CustomerAdminForm } from "./CustomerAdminForm";
import { AccountManagerSection } from "./AccountManagerSection";
import { validateStep2 } from "../../validation/step2Validation";
import { tenantOnboardingActions } from "../../store/tenantOnboardingStore";
import apiService from "~/services/service";
import API_ROUTES from "~/api-routes-v2";
import { AddAccountManagerModal } from "~/Pages/Tenants/modals/AddAccountManagerModal";
import ToastMessage from "~/components/ToastMessage";
import { triggerGlobalToast } from "~/utils/globalToast";
import type { CustomerAdminFormData } from "../../utils/formatters";
import type {
  AccountManagerFormData,
  AccountManagerOption,
  NewAccountManagerData,
} from "~/Pages/Tenants/types/tenants";

/**
 * Step 2: Admin Information Container
 */
export const Step2CustomerAdmin: React.FC = () => {
  // Toast ref for notifications
  const toastRef = useRef<any>(null);

  // State for account managers
  const [accountManagers, setAccountManagers] = useState<
    AccountManagerOption[]
  >([]);
  const [isLoadingAccountManagers, setIsLoadingAccountManagers] =
    useState(false);
  const [hasFetchedAccountManagers, setHasFetchedAccountManagers] =
    useState(false);

  // State for Add Account Manager modal
  const [showAddAccountManagerModal, setShowAddAccountManagerModal] =
    useState(false);
  const [isAddingAccountManager, setIsAddingAccountManager] = useState(false);

  // Fetch account managers from API (lazy loaded on dropdown open)
  const fetchAccountManagers = useCallback(async () => {
    if (hasFetchedAccountManagers || isLoadingAccountManagers) return;

    setIsLoadingAccountManagers(true);
    try {
      const response: any = await apiService(
        API_ROUTES.FETCH_ACCOUNT_MANAGERS,
        "GET",
      );
      if (response?.data) {
        const managers = response.data.map((manager: any) => ({
          id: manager.id,
          name:
            manager.preferred_name ||
            [manager.given_name, manager.surname].filter(Boolean).join(" "),
          email: manager.email,
        }));
        setAccountManagers(managers);
      }
      setHasFetchedAccountManagers(true);
    } catch (error) {
      console.error("Failed to fetch account managers:", error);
    } finally {
      setIsLoadingAccountManagers(false);
    }
  }, [hasFetchedAccountManagers, isLoadingAccountManagers]);

  const step2Data = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.step2Data,
  );
  const accountManagerData = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.accountManagerData,
  );
  const validationErrors = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.validationErrors,
  );
  const touchedFields = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.touchedFields,
  );

  const handleCustomerAdminChange = (
    updates: Partial<CustomerAdminFormData>,
  ) => {
    tenantOnboardingActions.updateStep2Data(updates);
    // Don't validate on change - only on blur or submit
  };

  const handleAccountManagerChange = (
    updates: Partial<AccountManagerFormData>,
  ) => {
    tenantOnboardingActions.updateAccountManagerData(updates);
    // Don't validate on change - only on blur or submit
  };

  const handleBlur = (fieldName: string) => {
    // Mark field as touched
    tenantOnboardingActions.markFieldTouched(fieldName);

    // Validate only touched fields
    const errors = validateStep2(step2Data);
    const filteredErrors: Record<string, string[]> = {};

    // Only show errors for touched fields
    Object.keys(errors).forEach((key) => {
      if (touchedFields.has(key) || fieldName === key) {
        filteredErrors[key] = errors[key];
      }
    });

    tenantOnboardingActions.setValidationErrors(filteredErrors);
  };

  const handleAddNewAccountManager = () => {
    setShowAddAccountManagerModal(true);
  };

  // Handle account manager selection from dropdown
  const handleAccountManagerSelect = (manager: AccountManagerOption) => {
    toastRef.current?.show({
      severity: "success",
      summary: `"${manager.name}"`,
      detail: (
        <span style={{ color: "var(--message-success-color, #1F2937)" }}>
          has been registered as Account Manager
        </span>
      ),
      life: 5000,
      icon: "pi pi-check",
    });
  };

  const handleAddAccountManagerSubmit = async (data: NewAccountManagerData) => {
    setIsAddingAccountManager(true);
    try {
      // Call API to create account manager
      const response: any = await apiService(
        API_ROUTES.CREATE_ACCOUNT_MANAGER,
        "POST",
        {
          body: {
            given_name: data.given_name,
            surname: data.surname || undefined,
            other_name: data.other_name || undefined,
            preferred_name: data.preferred_name || undefined,
            country: data.country,
            email: data.email,
            phone_code: data.phone_code || undefined,
            phone_number: data.phone_number || undefined,
          },
        },
      );

      if (response?.data) {
        // Add to local list with the returned ID
        const newManager: AccountManagerOption = {
          id: response.data.id,
          name: [response.data.given_name, response.data.surname]
            .filter(Boolean)
            .join(" "),
          email: response.data.email,
        };

        setAccountManagers((prev) => [...prev, newManager]);

        // Update the form with the new account manager
        tenantOnboardingActions.updateAccountManagerData({
          account_manager_id: newManager.id,
          account_manager_email: newManager.email,
          is_new_account_manager: true,
        });

        setShowAddAccountManagerModal(false);

        // Show success notification
        toastRef.current?.show({
          severity: "success",
          summary: `"${newManager.name}"`,
          detail: (
            <span style={{ color: "var(--message-success-color, #1F2937)" }}>
              has been registered as Account Manager registered.
            </span>
          ),
          life: 5000,
          icon: "pi pi-check",
        });
      }
    } catch (error) {
      console.error("Failed to add account manager:", error);
      // Show error notification using global toast (same as Connection Error toast)
      triggerGlobalToast({
        severity: "error",
        summary: "Error",
        detail: "We couldn't add the Account Manager. Please try again.",
        life: 5000,
      });
    } finally {
      setIsAddingAccountManager(false);
    }
  };

  return (
    <div className="admin-information-form">
      <p className="heading">Admin Information</p>
      <AccountManagerSection
        data={accountManagerData}
        onChange={handleAccountManagerChange}
        onBlur={handleBlur}
        errors={validationErrors}
        touchedFields={touchedFields}
        accountManagers={accountManagers}
        onAddNewAccountManager={handleAddNewAccountManager}
        onDropdownShow={fetchAccountManagers}
        onAccountManagerSelect={handleAccountManagerSelect}
        isLoading={isLoadingAccountManagers}
      />
      <CustomerAdminForm
        data={step2Data}
        onChange={handleCustomerAdminChange}
        onBlur={handleBlur}
        errors={validationErrors}
        touchedFields={touchedFields}
      />

      <AddAccountManagerModal
        visible={showAddAccountManagerModal}
        onHide={() => setShowAddAccountManagerModal(false)}
        onSubmit={handleAddAccountManagerSubmit}
        isLoading={isAddingAccountManager}
        existingEmails={accountManagers.map((m) => m.email.toLowerCase())}
      />

      <ToastMessage ref={toastRef} />
    </div>
  );
};
